// Freeze a manually-entered sale's pricing into the PDF snapshot at create/edit
// time. The snapshot captures the matched ATAP prices AS OF NOW so the quotation
// is locked — later ATAP price changes never retroactively alter this sale.
// Only an explicit edit (which calls this again) changes the amount.

import { methodBreakdown, termForMethod, paymentLabel, isMixMethod, mixBreakdown } from './ccPricing';
import { PER_BATTERY_PROMO, perBatteryPromo } from './rscPromos';

const num = (v) => Number(v) || 0;

// Parse "1p8k" / "3p8k" / "1p5k" -> 8 / 8 / 5 (kWac).
export function parseInverterKw(spec) {
    const m = /p\s*(\d+(?:\.\d+)?)\s*k/i.exec(String(spec || ''));
    if (m) return Number(m[1]);
    const m2 = /(\d+(?:\.\d+)?)/.exec(String(spec || ''));
    return m2 ? Number(m2[1]) : 0;
}

// Build the frozen system + pricing keys for a manual sale's snapshot.
// `match` = the matched Residential Pricing row (camelCase, may be null).
// `lineItems` = the add-on table rows (battery + other add-ons) so they render in
// the PDF scope of work and their cost folds into the FINAL totals.
export function buildFrozenPricing({
    match, is3p, energy, panelQty, originalPrice,
    asternDiscount, agentDiscount, uplineDiscount = 0, teamDiscount = 0, addOnPrice, lineItems = [],
    promoBackupbox, promoFirst640, promoPerBatt,
    paymentMethod, mixCashAmount = 0, mixTerm = 36,
    // The card's bank. Only 60 months uses it, where the rate is the bank's own;
    // absent, every figure below is exactly what it was before banks existed.
    ccBank = '', ccBankMethod = '',
}) {
    // All four discount buckets. Upline/Team were added 2026-08-19 and MUST be here:
    // this value drives the frozen quotation PDF, so omitting them prints a price
    // RM(upline+team) too high to the customer while every screen looks correct.
    const disc = num(asternDiscount) + num(agentDiscount) + num(uplineDiscount) + num(teamDiscount);

    // Cash base = the matched ATAP cash price now; fall back to the entered gross.
    let cashBase = match ? (num(is3p ? match.cash3p8k : match.cashPrice) || num(originalPrice)) : num(originalPrice);

    const kwac = parseInverterKw(match ? (is3p ? match.inverterSize3p : match.inverterSize) : '');
    const panelWatt = (num(panelQty) > 0 && num(energy) > 0)
        ? String(Math.round(num(energy) * 1000 / num(panelQty)))
        : '640';

    // Map add-on table rows: battery -> battery fields; everything else -> AddOnItems.
    const items = lineItems || [];
    const battery = items.find(li => li.isBattery);
    const addOnItems = items.filter(li => !li.isBattery).map(li => ({
        // Preserve qty 0 (e.g. backup-box removed) — don't default it to 1.
        // Carry the catalog description so it renders as bullets on the quotation.
        id: '', name: li.itemName || '', desc: li.description || li.desc || '',
        qty: (li.qty == null || li.qty === '') ? 1 : num(li.qty),
        price: num(li.unitPrice),
    }));
    // Add-on cost (battery + others) folds into the FINAL totals so the quotation's
    // headline price is the full amount the customer pays (system + add-ons).
    const addonTotal = num(addOnPrice);

    // Per-battery promo (PER_BATTERY_PROMO × 16kWh battery qty — RM 800 again since the
    // 2026-08-13 revert) is folded into the battery's NET line-item price and kept OUT
    // of the sale's discount columns ON PURPOSE: commission counts SYSTEM only, so an
    // add-on discount must never reduce the commissionable base (user 2026-06-19). For
    // the QUOTATION DISPLAY ONLY we surface it — show the battery at its GROSS price and
    // add the promo into the visible discount so the original / discount tally with the
    // RSC card. The sale's columns + line items stay untouched.
    const battQtyP = battery ? (num(battery.qty) || 1) : 0;
    const battPerUnitKwh = battQtyP > 0 ? num(battery.batteryKwh) / battQtyP : 0;
    const perBatt = (promoPerBatt && battery && Math.round(battPerUnitKwh) === 16) ? perBatteryPromo(battQtyP) : 0;

    // All three quotation lines use the SAME conversion as the create/edit Section-2
    // panel and the commission (utils/ccPricing.methodBreakdown): system & add-on
    // each round-up RM10, discounts rounded, final = system − discounts. This makes
    // cash / 36M / 60M tally with Section 2 exactly (user 2026-06-17).
    const bd = (term) => methodBreakdown({ originalPrice: cashBase, asternDiscount: num(asternDiscount), agentDiscount: num(agentDiscount), uplineDiscount: num(uplineDiscount), teamDiscount: num(teamDiscount), addOnPrice: addonTotal }, term, { bank: ccBank, bankMethod: ccBankMethod || null });
    const discountedCashPrice = bd(0).total;
    // The quotation shows the SELECTED main payment method only: cash always, plus the
    // chosen CC term (24M → 24M line). MIX is treated as cash (term 0).
    const quoteTermMonths = termForMethod(paymentMethod); // 0 = cash, else 12/24/36/60

    // MIX (cash + CC): the customer pays `mixCashAmount` up front and finances the REST
    // over `mixTerm` months. termForMethod() returns 0 for MIX, so the quotation keeps the
    // CASH headline (that's the price the discount story is told against) and these fields
    // drive an extra "TOTAL PAYABLE (MIX)" row underneath it. Without them the quotation
    // printed only the cash price and UNDERSTATED what the customer actually pays by the
    // whole CC markup — RM 28,990 shown vs RM 30,530 payable on a 10k/36M mix (user
    // 2026-08-22). mixBreakdown() is the same source of truth the Section-2 card and the
    // commission run on, so the printed total can never drift from the stored one.
    const mixTermN = Number(mixTerm) || 36;
    const mixBd = isMixMethod(paymentMethod)
        ? mixBreakdown({
            originalPrice: cashBase, asternDiscount: num(asternDiscount), agentDiscount: num(agentDiscount),
            uplineDiscount: num(uplineDiscount), teamDiscount: num(teamDiscount), addOnPrice: addonTotal,
        }, mixTermN, num(mixCashAmount))
        : null;

    const out = {
        priceSource: 'manual',          // marks this snapshot as column-derived (re-freezable on edit)
        kwp: num(energy),
        panelQty: num(panelQty),
        panelWatt,
        kwac,
        oriCashPrice: cashBase,
        // Display discount = system discount + the per-battery add-on promo (the latter
        // is shown on the quotation only; it is NOT in the sale's discount columns).
        cashDiscount: disc + perBatt,
        discountedCashPrice,
        cc36Discount: disc,
        cc36mPrice: bd(36).total,
        cc60Discount: disc,
        cc60mPrice: bd(60).total,
        // Selected-method quote: drives the single CC line + the "PAYMENT METHOD" label.
        quoteTermMonths,
        quoteTermPrice: quoteTermMonths > 0 ? bd(quoteTermMonths).total : 0,
        // MIX breakdown — all 0 for every other method, so the PDF row stays hidden and
        // non-MIX quotations are byte-identical. `mixCcPrice` is the FINANCED amount the
        // customer actually repays on the card (total − cash), NOT mixBreakdown's
        // `ccPortion`, which is the cash-basis remainder before the ÷factor markup.
        mixCashPortion: mixBd ? mixBd.cashPortion : 0,
        mixCcPrice: mixBd ? Math.max(0, mixBd.total - mixBd.cashPortion) : 0,
        mixTotal: mixBd ? mixBd.total : 0,
        mixQuoteTerm: mixBd ? mixTermN : 0,
        paymentMethodLabel: paymentLabel(paymentMethod),
        addOnPrice: addonTotal,
        addOnItems,
        // Persist the CASH Section-1 inputs the agent actually typed, so the edit
        // page can repopulate Section 1 (otherwise only the converted method values
        // survive on the sale record and the cash "disappears" on reopen).
        section1Cash: {
            originalPrice: num(originalPrice),
            asternDiscount: num(asternDiscount),
            agentDiscount: num(agentDiscount),
            addOnPrice: num(addOnPrice),
        },
        // Persist promo toggles so the sale page can restore them on edit.
        promoBackupbox: !!promoBackupbox,
        promoFirst640: !!promoFirst640,
        promoPerBatt: !!promoPerBatt,
    };
    // ALWAYS set the battery fields — including to 0 when there's no battery — so a
    // re-freeze that merges over the previous snapshot ({...formPayload, ...out})
    // CLEARS a removed battery instead of keeping the stale row in the quotation.
    out.batteryKwh = battery ? (num(battery.batteryKwh) || 16) : 0;
    out.batteryQty = battery ? (num(battery.qty) || 1) : 0;
    // Quotation displays the battery at GROSS (net + the per-battery promo). The promo
    // shows as part of cashDiscount above; the customer's FINAL price is unchanged.
    out.batteryCashPrice = battery ? num(battery.unitPrice) + (perBatt > 0 ? PER_BATTERY_PROMO : 0) : 0;
    return out;
}
